import {
  Component,
  computed,
  HostBinding,
  inject,
  model,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TRANSLATION_PREFIX, TPipe } from '@pipes/t.pipe';
import {
  AbbTableComponent,
  DataTableFilter,
  FilterOption,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { AbbStatusIconComponent } from '@components/abb-status-icon/abb-status-icon.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  Action_Type,
  CategorySelectOptionsQuery,
  CategorySelectOptionsQueryVariables,
  ControlListQuery,
  ControlListQueryVariables,
  GroupSelectOptionsQuery,
  GroupSelectOptionsQueryVariables,
  Status,
  ToggleOnoffCtrlMutation,
  ToggleOnoffCtrlMutationVariables,
  UpdateActionCtrlValueMutation,
  UpdateActionCtrlValueMutationVariables,
} from 'gql/graphql';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, first, Subscription } from 'rxjs';
import { dataProviderFromObservable } from '@utils/data-provider';
import { AbbSwitchComponent } from '@components/abb-switch/abb-switch.component';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { groupSelectOptionsQuery } from '@graphql/group';
import { categorySelectOptionsQuery } from '@graphql/category';
import {
  controlListQuery,
  toggleONOFFCtrlMutation,
  updateActionCtrlValueMutation,
} from '@graphql/controls';
import {
  ACTION_TYPE_DICTIONARY,
  ActionType,
  CONTROLS_SUBTITLE,
} from '@pages/controls/controls';
import { TranslateService } from '@ngx-translate/core';
import { MatSliderModule } from '@angular/material/slider';
import { CommonModule } from '@angular/common';
import { OverlayService } from '@services/ui/overlay.service';
import { ToastService } from '@services/ui/toast.service';
import { LOADING } from '@utils/loader';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';

export interface ControlDataTableFilter {
  deviceName: string;
  categoryId: string;
  groupId: string;
}

@Component({
  selector: 'abb-manual-control',
  standalone: true,
  imports: [
    CommonModule,
    AbbTableComponent,
    AbbStatusIconComponent,
    TPipe,
    MatTooltipModule,
    AbbSwitchComponent,
    FormsModule,
    RouterModule,
    MatSliderModule,
    OverflowTooltipDirective,
  ],
  template: `
    <abb-table
      [dataProvider]="dataProvider"
      [columns]="columns()"
      [defaultFilters]="defaultFilters"
      [filters]="filters()"
      [loading]="loading()"
    ></abb-table>
    <ng-template #status let-item>
      <abb-status-icon
        class="inline-flex"
        variant="outlined"
        size="small"
        [status]="item.status"
        [matTooltip]="
          (item.status === Status.Ok
            ? 'Connected'
            : item.status === Status.Warning
            ? 'Offline'
            : 'Needs intervention'
          ) | t
        "
      ></abb-status-icon>
    </ng-template>
    <ng-template #device let-item>
      <a
        overflowTooltip
        [routerLink]="'/asset-management/devices/' + [item.device.id]"
        class="text-abb-primary/80 truncate block font-medium hover:text-abb-primary transition-colors cursor-pointer"
        >{{ item.device.name }}</a
      >
    </ng-template>
    <ng-template #group let-item>
      <span overflowTooltip class="block truncate">
        @for (g of item.groups; track g.name) {
        {{ g.name }}@if (!$last) {, } }
      </span>
    </ng-template>
    <ng-template #category let-item>
      <span overflowTooltip class="block truncate">{{
        item.category.name | t
      }}</span>
    </ng-template>
    <ng-template #isActive let-item>
      <div class="flex justify-center items-center">
        <abb-switch
          id="control-power"
          [(ngModel)]="isActiveCtrlValue()[item.id]"
          (click)="openConfirmModal(item.id, $event)"
        ></abb-switch>
      </div>
    </ng-template>
    <!-- <ng-template #action let-item>
      <div class="block" [ngClass]="{ 'pt-1.5': item.action != null }">
        @if(item.action != null) {
        <div class="w-full flex flex-row justify-center relative">
          <mat-slider
            [ngClass]="{
              '!pointer-events-none !cursor-auto': !isActiveCtrlValue()[item.id]
            }"
            class="small-slider light-disabled-slider relative"
            [showTickMarks]="item.action.type === Action_Type.ChargingSpeed"
            [disabled]="!isActiveCtrlValue()[item.id]"
            [min]="
              isActiveCtrlValue()[item.id]
                ? item.action.min
                : ACTION_TYPE_DICTIONARY[item.action.type].defaultMin.value
            "
            [max]="
              isActiveCtrlValue()[item.id]
                ? item.action.max
                : ACTION_TYPE_DICTIONARY[item.action.type].defaultMax.value
            "
          >
            <div
              class="text-2xs text-abb-font absolute top-0 left-0"
              [ngClass]="
                !isActiveCtrlValue()[item.id] ? 'opacity-100' : 'opacity-60'
              "
            >
              {{ ACTION_TYPE_DICTIONARY[item.action.type].label | t }}
            </div>
            <input
              matSliderThumb
              id="slider-temperature"
              [ngModel]="
                isActiveCtrlValue()[item.id]
                  ? actionCtrlValue()[item.id]
                  : ACTION_TYPE_DICTIONARY[item.action.type].defaultValue
              "
              (ngModelChange)="updateActionCtrlModel(item.id, $event)"
              (pointerup)="updateActionCtrlValue(item.id, $event)"
            />
            <div
              class="absolute text-2xs text-abb-font bottom-1 left-0"
              [ngClass]="
                !isActiveCtrlValue()[item.id] ? 'opacity-100' : 'opacity-60'
              "
            >
              {{
                isActiveCtrlValue()[item.id] &&
                !ACTION_TYPE_DICTIONARY[item.action.type]
                  .showAlwaysDefaultLabels
                  ? item.action.min + item.action.measure
                  : ACTION_TYPE_DICTIONARY[item.action.type].defaultMin.label
              }}
            </div>
            <div
              class="absolute text-2xs text-abb-font bottom-1 right-0"
              [ngClass]="
                !isActiveCtrlValue()[item.id] ? 'opacity-100' : 'opacity-60'
              "
            >
              {{
                isActiveCtrlValue()[item.id] &&
                !ACTION_TYPE_DICTIONARY[item.action.type]
                  .showAlwaysDefaultLabels
                  ? item.action.max + item.action.measure
                  : ACTION_TYPE_DICTIONARY[item.action.type].defaultMax.label
              }}
            </div>
          </mat-slider>
        </div>
        } @else {
        <div class="text-center text-xl">{{ '--' }}</div>
        }
      </div>
    </ng-template> -->
  `,
  styles: `
    .small-slider {
      --mdc-slider-handle-height: 16px;
      --mdc-slider-handle-width: 16px;
    }
    .light-disabled-slider {
      --mdc-slider-disabled-active-track-color: #9e9e9e;
      --mdc-slider-disabled-handle-color: #9e9e9e;
    }
    .mdc-slider {
      height: 56px;
    }
    ::ng-deep .mdc-slider__input {
      height: 52px !important;
    }
    ::ng-deep .mdc-slider .mdc-slider__thumb {
      height: 56px !important;
    }
  `,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'controls.tabs.manual_control',
    },
  ],
})
export class ManualControlComponent implements OnDestroy {
  @HostBinding('attr.id') componentid = 'manuel-control-component';
  readonly Status = Status;
  readonly Action_Type = Action_Type;
  readonly ACTION_TYPE_DICTIONARY: ActionType = ACTION_TYPE_DICTIONARY;
  subtitle = inject(CONTROLS_SUBTITLE);
  controls$ = new BehaviorSubject<ControlListQuery['controlList'] | []>([]);
  isActiveCtrlValue = model<Record<string, boolean | undefined>>({});
  actionCtrlValue = model<Record<string, number | undefined>>({});

  groupSelectOptions = signal<FilterOption[]>([]);
  categorySelectOptions = signal<FilterOption[]>([]);
  filters = computed<DataTableFilter<ControlDataTableFilter>[]>(() => {
    return [
      {
        id: 'deviceName',
        filterType: 'text',
        className: 'w-full',
        label: 'Device Name',
      },
      {
        id: 'categoryId',
        filterOptions: this.categorySelectOptions(),
        filterType: 'select',
        className: 'w-full',
        label: 'Device Category',
      },
      {
        id: 'groupId',
        filterOptions: this.groupSelectOptions(),
        filterType: 'select',
        className: 'w-full',
        label: 'Group',
      },
    ];
  });
  defaultFilters: ControlDataTableFilter = {
    deviceName: '',
    categoryId: '',
    groupId: '',
  };
  dataProvider = dataProviderFromObservable<
    ControlListQuery['controlList'][number],
    ControlDataTableFilter
  >(this.controls$, (el, filter) => {
    return (
      (!filter.categoryId || el.category.id === filter.categoryId) &&
      (!filter.groupId ||
        (el.groups?.some((g) => g.id == filter.groupId) ?? false)) &&
      el.device.name.toLowerCase().includes(filter.deviceName.toLowerCase())
    );
  });

  statusTemplate = viewChild<TemplateRef<unknown>>('status');
  deviceNameTemplate = viewChild<TemplateRef<unknown>>('device');
  categoryTemplate = viewChild<TemplateRef<unknown>>('category');
  groupTemplate = viewChild<TemplateRef<unknown>>('group');
  isActiveTemplate = viewChild<TemplateRef<unknown>>('isActive');
  actionTemplate = viewChild<TemplateRef<unknown>>('action');

  columns = computed<IColumn<ControlListQuery['controlList'][number]>[]>(() => {
    return [
      {
        id: 'id',
        name: 'Id',
        key: 'id',
        translate: true,
        hidden: true,
      },
      {
        id: 'status',
        name: 'Status',
        translate: true,
        templateRef: this.statusTemplate(),
        tableHeaderCellClass: 'w-0',
      },
      {
        id: 'device',
        name: 'Name',
        templateRef: this.deviceNameTemplate(),
        translate: true,
      },
      {
        id: 'category',
        name: 'Device Category',
        templateRef: this.categoryTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'groups',
        name: 'Group',
        templateRef: this.groupTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
      },
      {
        id: 'isActive',
        name: 'Off/On',
        templateRef: this.isActiveTemplate(),
        translate: true,
        tableCellClass: 'font-medium',
        tableHeaderCellClass: '[&>*]:!justify-center',
      },
      // {
      //   id: 'action',
      //   name: 'Action',
      //   templateRef: this.actionTemplate(),
      //   translate: true,
      //   tableHeaderCellClass: '[&>*]:!justify-center',
      // },
    ];
  });
  loading = signal(true);
  prefix = inject(TRANSLATION_PREFIX);

  private subscriptions: Subscription[] = [];
  private currentSuccessToast: any = null;
  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly overlayService: OverlayService,
    private readonly toast: ToastService
  ) {
    this.subscriptions.push(
      apollo
        .watchQuery<ControlListQuery, ControlListQueryVariables>({
          query: controlListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.controls$.next(
              [...data.controlList].sort((a, b) => {
                // TODO: se non è modificabile dall'utente andrebbe fatto direttamente a BE
                // se è modificabile dall'utente, andrebbe gestito nel dataProvider
                if (a.status === b.status) {
                  return a.device.name < b.device.name ? -1 : 1;
                }
                if (a.status === Status.Alarm) {
                  return -1;
                }
                if (b.status === Status.Alarm) {
                  return 1;
                }
                if (a.status === Status.Warning) {
                  return -1;
                }
                if (b.status === Status.Warning) {
                  return 1;
                }
                return 0;
              })
            );
            this.isActiveCtrlValue.set(
              data.controlList.reduce((acc, curr) => {
                return {
                  ...acc,
                  [curr.id]: curr.isActive,
                };
              }, {})
            );
            this.actionCtrlValue.set(
              data.controlList.reduce((acc, curr) => {
                return {
                  ...acc,
                  [curr.id]: curr.action?.value,
                };
              }, {})
            );
            this.subtitle.set({
              key: `You have a total of {tot} devices to be controlled`,
              params: {
                tot: data.controlList.length,
              },
            });
            this.loading.set(loading);
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<GroupSelectOptionsQuery, GroupSelectOptionsQueryVariables>({
          query: groupSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.groupSelectOptions.set(
              data.groupList.map((group) => ({
                value: group.id,
                label: group.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<
          CategorySelectOptionsQuery,
          CategorySelectOptionsQueryVariables
        >({ query: categorySelectOptionsQuery })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.categorySelectOptions.set(
              data.categoryList.map((cat) => ({
                value: cat.id,
                label: cat.name,
                skipTranslation: false,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  awaitingRes = inject(LOADING);
  openConfirmModal(id: string, event: Event) {
    event.preventDefault();
    const row = this.controls$.value?.find((c) => c.id === id);
    const from = row?.isActive ? 'on' : 'off';
    const to = row?.isActive ? 'off' : 'on';
    this.overlayService
      .openAlert({
        message: `${t(
          this.prefix,
          'Are you sure that you want to change the status from {fromStatus} to {toStatus} of the',
          this.translate,
          { fromStatus: from.toUpperCase(), toStatus: to.toUpperCase() }
        )} <b>${row?.device.name}</b>?`,
        htmlMessage: true,
        confirmText: t(this.prefix, 'Turn {toStatus}', this.translate, {
          toStatus: to,
        }),
      })
      .then(() => {
        this.awaitingRes.set(true);
        const newValue = !row?.isActive;
        this.updateIsActiveCtrlValue(id, newValue).finally(() =>
          this.awaitingRes.set(false)
        );
      })
      .catch(() => console.log('cancel'));
  }

  updateIsActiveCtrlValue(id: string, newValue: boolean | undefined) {
    return new Promise<void>((resolve, reject) => {
      const row = this.controls$.value?.find((c) => c.id === id);
      if (newValue !== undefined && newValue !== row?.isActive) {
        this.toast.dismissCurrentToast();

        this.apollo
          .mutate<ToggleOnoffCtrlMutation, ToggleOnoffCtrlMutationVariables>({
            mutation: toggleONOFFCtrlMutation,
            variables: {
              id,
              isActive: newValue,
            },
          })
          .pipe(first())
          .subscribe({
            next: () => {
              this.apollo.client
                .refetchQueries({
                  include: ['ControlList'],
                })
                .then(() => {
                  this.toast.notifySuccess(
                    t(
                      this.prefix,
                      `{deviceName} was turned {devicePowerStatus} successfully`,
                      this.translate,
                      {
                        devicePowerStatus: newValue ? 'on' : 'off',
                        deviceName: `<b>${row?.device.name}</b>`,
                      }
                    ),
                    { htmlMessage: true }
                  );
                });
              resolve();
            },
            error: () => {
              this.toast.notifyError(
                t(
                  this.prefix,
                  `An error occurred while turning device {devicePowerStatus}`,
                  this.translate,
                  { devicePowerStatus: newValue ? 'on' : 'off' }
                )
              );
              reject();
            },
          });
      }
    });
  }

  updateActionCtrlModel(id: string, newValue: number) {
    this.actionCtrlValue.update((old) => {
      return { ...old, [id]: newValue };
    });
  }

  updateActionCtrlValue(id: string, event: Event) {
    if (this.isActiveCtrlValue()[id]) {
      const row = this.controls$.value?.find((c) => c.id === id);
      const newValue = parseFloat((event.target as HTMLInputElement).value);
      if (newValue !== undefined && newValue !== row?.action?.value) {
        this.apollo
          .mutate<
            UpdateActionCtrlValueMutation,
            UpdateActionCtrlValueMutationVariables
          >({
            mutation: updateActionCtrlValueMutation,
            variables: {
              id,
              value: newValue,
            },
          })
          .pipe(first())
          .subscribe();
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    // Dismiss any active toast when component is destroyed
    this.toast.dismissCurrentToast();
  }
}
